/**
 * Question Handler System
 * Manages all question types, decision methods, and answer processing
 * Based on game_model.yaml questions and decision_methods sections
 */

import {
  Question,
  QUESTIONS,
  QuestionId,
  PlayerDecision,
  TeamDecision,
  AnswerValue
} from '../models/Question';
import { GameState } from '../models/GameState';

export class QuestionHandler {
  /**
   * Get question by ID
   */
  getQuestion(questionId: QuestionId): Question {
    const question = QUESTIONS[questionId];
    if (!question) {
      throw new Error(`Question ${questionId} not found`);
    }
    return question;
  }

  /**
   * Check if question is available for a specific player
   */
  isQuestionAvailableForPlayer(
    questionId: QuestionId,
    playerId: string,
    gameState: GameState
  ): boolean {
    const question = this.getQuestion(questionId);
    const player = gameState.players.find(p => p.playerId === playerId);

    if (!player) return false;

    // Check conditional availability (e.g., Q12B, Q13B, Q14B only for swimmers)
    if (question.onlyFor) {
      if (question.onlyFor.includes('Q11B')) {
        // Only for players who chose to swim
        const q11bAnswer = player.decisions.get('Q11B');
        return q11bAnswer === 'B';
      }
    }

    return true;
  }

  /**
   * Process individual answer
   */
  processIndividualAnswer(
    questionId: QuestionId,
    playerId: string,
    answer: AnswerValue,
    gameState: GameState
  ): PlayerDecision {
    const question = this.getQuestion(questionId);

    // Validate answer based on question type
    this.validateAnswer(question, answer, gameState);

    return {
      playerId,
      questionId,
      answer,
      timestamp: new Date()
    };
  }

  /**
   * Validate answer against question constraints
   */
  private validateAnswer(
    question: Question,
    answer: AnswerValue,
    gameState: GameState
  ): void {
    switch (question.type) {
      case 'text_input':
        if (typeof answer !== 'string') {
          throw new Error(`Question ${question.id} expects a string answer`);
        }
        break;

      case 'multiple_choice':
        if (!question.options) {
          throw new Error(`Question ${question.id} has no options defined`);
        }
        const validOptions = question.options.map(opt => opt.id);
        if (!validOptions.includes(answer as string)) {
          throw new Error(`Invalid option ${answer} for question ${question.id}`);
        }
        break;

      case 'number_input':
        if (typeof answer !== 'number') {
          throw new Error(`Question ${question.id} expects a number answer`);
        }
        if (question.min !== undefined && answer < question.min) {
          throw new Error(`Answer ${answer} is below minimum ${question.min}`);
        }
        if (question.max !== undefined) {
          const maxValue = typeof question.max === 'string'
            ? gameState.teamEnergyPacks
            : question.max;
          if (answer > maxValue) {
            throw new Error(`Answer ${answer} exceeds maximum ${maxValue}`);
          }
        }
        break;

      case 'item_selection':
        // Validate item exists in inventory
        const itemId = answer as string;
        const hasItem = gameState.inventory.some(item => item.id === itemId && item.quantity > 0);
        if (!hasItem) {
          throw new Error(`Item ${itemId} not available in inventory`);
        }
        break;
    }
  }

  /**
   * Process team decision using appropriate decision method
   */
  processTeamDecision(
    questionId: QuestionId,
    playerDecisions: PlayerDecision[],
    _gameState: GameState
  ): TeamDecision {
    const question = this.getQuestion(questionId);
    const decisionMethod = question.decisionMethod || 'majority_vote';

    let finalAnswer: AnswerValue | undefined;

    switch (decisionMethod) {
      case 'majority_vote':
        finalAnswer = this.majorityVote(playerDecisions);
        break;

      case 'majority_vote_exclude_neutral':
        finalAnswer = this.majorityVoteExcludeNeutral(
          playerDecisions,
          question.neutralOption || 'B'
        );
        break;

      case 'unanimous_A':
        finalAnswer = this.unanimousA(playerDecisions);
        break;

      case 'consensus':
        finalAnswer = this.consensus(playerDecisions);
        break;

      case 'individual':
        // Individual decisions don't need team-level processing
        finalAnswer = undefined;
        break;

      default:
        throw new Error(`Unknown decision method: ${decisionMethod}`);
    }

    return {
      questionId,
      playerDecisions,
      finalAnswer,
      decisionMethod,
      timestamp: new Date()
    };
  }

  /**
   * Majority vote - most common answer wins
   */
  private majorityVote(decisions: PlayerDecision[]): AnswerValue {
    const counts = new Map<AnswerValue, number>();

    for (const decision of decisions) {
      const current = counts.get(decision.answer) || 0;
      counts.set(decision.answer, current + 1);
    }

    let maxCount = 0;
    let winningAnswer: AnswerValue | undefined;
    let firstSubmitted: AnswerValue | undefined;

    for (const decision of decisions) {
      const count = counts.get(decision.answer) || 0;

      if (count > maxCount) {
        maxCount = count;
        winningAnswer = decision.answer;
        firstSubmitted = decision.answer;
      } else if (count === maxCount && !firstSubmitted) {
        // Tie breaker: first submitted
        firstSubmitted = decision.answer;
      }
    }

    return winningAnswer || firstSubmitted || decisions[0].answer;
  }

  /**
   * Majority vote excluding neutral option
   */
  private majorityVoteExcludeNeutral(
    decisions: PlayerDecision[],
    neutralOption: string
  ): AnswerValue {
    const filteredDecisions = decisions.filter(d => d.answer !== neutralOption);

    if (filteredDecisions.length === 0) {
      // All chose neutral - default to neutral
      return neutralOption;
    }

    return this.majorityVote(filteredDecisions);
  }

  /**
   * Unanimous A - all must select A to proceed
   */
  private unanimousA(decisions: PlayerDecision[]): AnswerValue {
    const allA = decisions.every(d => d.answer === 'A');
    return allA ? 'A' : 'B';
  }

  /**
   * Consensus - all must enter the same value
   */
  private consensus(decisions: PlayerDecision[]): AnswerValue {
    // Find most common answer
    return this.majorityVote(decisions);
  }

  /**
   * Check if all players have answered a question
   */
  hasAllPlayersAnswered(
    questionId: QuestionId,
    gameState: GameState
  ): boolean {
    for (const player of gameState.players) {
      // Check if question is available for this player
      if (!this.isQuestionAvailableForPlayer(questionId, player.playerId, gameState)) {
        continue;
      }

      // Check if player has answered
      if (!player.decisions.has(questionId)) {
        return false;
      }
    }

    return true;
  }

  /**
   * Check if consensus is reached for a consensus question
   */
  hasConsensus(
    questionId: QuestionId,
    gameState: GameState
  ): boolean {
    const question = this.getQuestion(questionId);

    if (!question.requiresConsensus) {
      return true; // Not a consensus question
    }

    const answers = gameState.players
      .map(p => p.decisions.get(questionId))
      .filter(a => a !== undefined);

    if (answers.length === 0) {
      return false;
    }

    // All answers must be the same
    const firstAnswer = answers[0];
    return answers.every(a => a === firstAnswer);
  }

  /**
   * Get decision status for a question
   */
  getDecisionStatus(
    questionId: QuestionId,
    gameState: GameState
  ): {
    answered: number;
    total: number;
    complete: boolean;
    consensus: boolean;
  } {
    const question = this.getQuestion(questionId);

    let total = 0;
    let answered = 0;

    for (const player of gameState.players) {
      if (this.isQuestionAvailableForPlayer(questionId, player.playerId, gameState)) {
        total++;
        if (player.decisions.has(questionId)) {
          answered++;
        }
      }
    }

    const allAnswered = answered === total;
    const hasConsensus = question.requiresConsensus
      ? this.hasConsensus(questionId, gameState)
      : true;

    return {
      answered,
      total,
      complete: allAnswered && hasConsensus,
      consensus: hasConsensus
    };
  }

  /**
   * Get unanswered players for a question
   */
  getUnansweredPlayers(
    questionId: QuestionId,
    gameState: GameState
  ): string[] {
    const unanswered: string[] = [];

    for (const player of gameState.players) {
      if (
        this.isQuestionAvailableForPlayer(questionId, player.playerId, gameState) &&
        !player.decisions.has(questionId)
      ) {
        unanswered.push(player.playerId);
      }
    }

    return unanswered;
  }
}

// Singleton instance
export const questionHandler = new QuestionHandler();
